<script lang="ts" setup>
  import { useMutation } from '@tanstack/vue-query'

  import type { LoginDto } from '~/core/services'
  import { useAuthService } from '~/core/services'
  import type { FetchErrorObj } from '~/core/types'

  const emit = defineEmits<{
    (e: 'close' | 'register' | 'reset'): void
  }>()

  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const { signInUser } = useAuthService()
  const { getUserAsync } = useUserStore()
  const { refetchCart } = useCartStore()

  const email = ref('')
  const password = ref('')

  const {
    mutate: login,
    isPending: isPendingLogin,
    error,
  } = useMutation<unknown, FetchErrorObj<LoginDto>, LoginDto>({
    mutationFn: (p: LoginDto) => signInUser(p),
    onSuccess: async () => {
      await getUserAsync()
      refetchCart()
      if (route.query.redirect as string) {
        await router.push(route.query.redirect as string)
      } else if (route.name && ['login', 'validate'].includes(route.name as string)) {
        router.push({ query: { ...route.query, redirect: 'index' } })
      } else {
        emit('close')
      }
    },
  })

  function handleLogin() {
    login({
      email: email.value,
      password: password.value,
      rememberMe: true,
    })
  }
  function register() {
    emit('register')
  }

  function reset() {
    emit('reset')
  }

  function submitForm() {
    return
  }

</script>
<template>
  <div class="c-login-form">
    <div class="title">
      <SvgIcon
        icon="account"
        :width="22"
        :height="22"
        :desktop-height="32"
        :desktop-width="32"
      />
      <span>{{ t("authentication.connect") }}</span>
    </div>
    <div class="sub-title">
      <span>{{ t("authentication.sub-title") }}</span>
    </div>
    <form
      class="form"
      action="#"
      @submit.prevent="submitForm"
    >
      <!-- <InputErrors
        v-if="error"
        :errors="[error.message]"
      /> -->
      <div class="form-top">
        <InputBase
          v-model="email"
          name="email"
          class="input"
          type="email"
          rules="required|email"
          :label="t('common.email')"
          value="exemple@email.com"
          :errors="error?.errors?.email"
        />
        <InputBase
          v-model="password"
          name="password"
          class="input"
          type="password"
          rules="required"
          :label="t('common.password')"
          value=""
          :errors="error?.errors?.password"
        />
        <a
          href="javascript:void(0)"
          class="small-link-underline"
          @click="reset"
        >
          <span>{{ t("authentication.forgotten-password") }}</span>
        </a>
      </div>
      <ButtonAndLink
        class="form-bottom"
        :disabled="isPendingLogin"
        button-type="submit"
        @button-click="handleLogin"
      >
        <template #button>
          {{ isPendingLogin ? "Connexion..." : t("authentication.login") }}
        </template>
        <template #caption>
          {{ t("authentication.no-account") }}
        </template>
        <template #link>
          <a
            href="javascript:void(0)"
            @click="register"
          >
            {{ t("authentication.register") }}
          </a>
        </template>
      </ButtonAndLink>
    </form>
  </div>
</template>

<style scoped>

.c-login-form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  padding: 1.5em;
  overflow-y: auto;

  .title {
    display: flex;
    flex: 1 1 auto;
    flex-direction: row;
    align-items: center;

    span {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1.125em;
      font-weight: bold;
    }

    .c-icon.account {
      margin-right: 1em;
      color: var(--color-primary);
    }
  }

  .sub-title {
    flex: 0 0 auto;
    align-self: flex-start;
    margin-top: 1em;

    span {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 600;
      opacity: 0.8;
    }
  }

  .form {
    position: relative;
    display: flex;
    width: 100%;
    flex: 1 0 auto;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 2em;

    .input {
      margin-top: 1em;
    }

    .small-link-underline {
      display: flex;
      margin-top: 1em;
      text-decoration: underline;

      span {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 0.875em;
      }
    }
  }
}

@media screen and (width >= 1024px) {
  .c-login-form {
    padding: 0;

    .title {
      span {
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 2.5em;
        font-weight: bold;
      }

      .c-icon.account {
        margin-right: 1.25rem;
      }
    }

    .sub-title {
      span {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1.25em;
        font-weight: 600;
      }
    }

    .form {
      .form-bottom {
        .button {
          font-size: 1.5em;
        }

        .caption {
          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
          }
        }

        .link-underline {
          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1.25em;
          }
        }
      }
    }
  }
}
</style>
